import {useQuery} from '@github-ui/react-query'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import type {AgentInfo} from '@github-ui/agent-sessions-types/agent-identity'
import {getAgentInfoBySlug} from './agent-info-registry'

export function generateAgentInfoQueryKey(agentId: number | undefined | null, hasProfileInfo: boolean) {
  return ['agent-info', agentId, hasProfileInfo]
}

/**
 * Fetches agent metadata (name, avatar, etc.) from the monolith for agents
 * returned from CAPI sessions that don't include full agent information.
 *
 * For agents with known profile info (e.g. CLI), returns that directly
 * without a network request.
 */
export function useAgentInfo(agentId: number | undefined | null, agentSlug?: string) {
  const profileInfo = getAgentInfoBySlug(agentSlug)

  return useQuery<AgentInfo | null>({
    queryKey: generateAgentInfoQueryKey(agentId, !!profileInfo),
    queryFn: async () => {
      if (profileInfo) {
        return profileInfo
      }

      const response = await reactFetchJSON(`/copilot/agent-sessions/agents/${agentId}`)

      if (!response.ok) {
        // For server errors (5xx), throw to allow retries since they're transient.
        if (response.status >= 500) {
          throw new Error(`Failed to fetch agent info for agent ${agentId}: ${response.status}`)
        }
        // For client errors (e.g. 404), return null to prevent infinite refetch loops.
        // TanStack Query v5 treats queries with data === undefined as always stale,
        // so throwing would cause repeated refetches regardless of staleTime.
        return null
      }

      const data = await response.json()
      return data.agent_info
    },
    enabled: !!agentId,
    staleTime: Infinity,
    retry: 3,
  })
}
